import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import api from "../../utils/axios";
import { useSubscription } from "../../contexts/SubscriptionContext";

export default function InviteLanding() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, isLoading: authLoading, loginWithRedirect } = useAuth0();
  const { refreshSubscription } = useSubscription();

  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [accepting, setAccepting] = useState(false);

  // Fetch public invite info
  useEffect(() => {
    if (!token) return;
    let cancelled = false;

    (async () => {
      try {
        const { data } = await api.get(`/api/org/invite/${token}/info`);
        if (!cancelled) {
          setInvite(data.invitation);
          setLoading(false);
        }
      } catch (err) {
        if (!cancelled) {
          setError(
            err.response?.status === 404
              ? "This invitation wasn't found. It may have been revoked."
              : "Something went wrong loading this invitation."
          );
          setLoading(false);
        }
      }
    })();

    return () => { cancelled = true; };
  }, [token]);

  // If already authenticated, accept the invite directly
  useEffect(() => {
    if (!isAuthenticated || !invite || invite.status !== "pending" || accepting) return;
    let cancelled = false;

    (async () => {
      setAccepting(true);
      try {
        const { acceptInvitation } = await import("../../services/subscriptionService");
        const result = await acceptInvitation(token);
        if (!cancelled && result.success) {
          await refreshSubscription();
          navigate(`/app/settings?tab=workspace`, { replace: true });
        }
      } catch (err) {
        if (!cancelled) {
          setError("Failed to accept the invitation. It may have already been used.");
          setAccepting(false);
        }
      }
    })();

    return () => { cancelled = true; };
  }, [isAuthenticated, invite, token, accepting, refreshSubscription, navigate]);

  const handleJoin = () => {
    loginWithRedirect({
      appState: { returnTo: `/invite/${token}` },
      authorizationParams: {
        screen_hint: "signup",
        login_hint: invite?.emailFull || "",
      },
    });
  };

  const handleLogin = () => {
    loginWithRedirect({
      appState: { returnTo: `/invite/${token}` },
      authorizationParams: {
        login_hint: invite?.emailFull || "",
      },
    });
  };

  // --- Render ---

  if (loading || authLoading) {
    return (
      <Shell>
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-slate-200 rounded w-48 mx-auto" />
          <div className="h-4 bg-slate-200 rounded w-64 mx-auto" />
          <div className="h-12 bg-slate-200 rounded-xl w-40 mx-auto mt-8" />
        </div>
      </Shell>
    );
  }

  if (accepting) {
    return (
      <Shell>
        <Spinner />
        <h2 className="text-xl font-bold text-slate-900 mt-4">Joining the team...</h2>
        <p className="text-slate-500 mt-1 text-sm">Setting up your account now.</p>
      </Shell>
    );
  }

  if (error || !invite) {
    return (
      <Shell>
        <div className="w-14 h-14 rounded-full bg-red-50 flex items-center justify-center mx-auto">
          <svg className="w-7 h-7 text-red-400" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
          </svg>
        </div>
        <h2 className="text-xl font-bold text-slate-900 mt-4">Invitation unavailable</h2>
        <p className="text-slate-500 mt-2 text-sm max-w-sm mx-auto">{error || "This invitation could not be found."}</p>
        <button
          onClick={() => navigate("/app")}
          className="mt-6 px-6 py-2.5 text-sm font-medium text-slate-700 bg-slate-100 rounded-xl hover:bg-slate-200 transition-colors"
        >
          Go to Scout
        </button>
      </Shell>
    );
  }

  if (invite.status === "expired") {
    return (
      <Shell>
        <div className="w-14 h-14 rounded-full bg-amber-50 flex items-center justify-center mx-auto">
          <svg className="w-7 h-7 text-amber-400" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <h2 className="text-xl font-bold text-slate-900 mt-4">Invitation expired</h2>
        <p className="text-slate-500 mt-2 text-sm max-w-sm mx-auto">
          This invitation to <strong>{invite.orgName}</strong> has expired.
          Ask your team admin to send a new one.
        </p>
      </Shell>
    );
  }

  if (invite.status !== "pending") {
    return (
      <Shell>
        <div className="w-14 h-14 rounded-full bg-green-50 flex items-center justify-center mx-auto">
          <svg className="w-7 h-7 text-green-500" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <h2 className="text-xl font-bold text-slate-900 mt-4">Already accepted</h2>
        <p className="text-slate-500 mt-2 text-sm">This invitation has already been used.</p>
        <button
          onClick={() => navigate("/app")}
          className="mt-6 px-6 py-2.5 text-sm font-medium text-white bg-[#5a20ff] rounded-xl hover:bg-[#4a1ad6] transition-colors"
        >
          Open Scout
        </button>
      </Shell>
    );
  }

  // --- Main: pending invite, user not authenticated ---
  return (
    <Shell>
      {/* Team avatar */}
      <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#5a20ff] to-[#52dcb3] flex items-center justify-center mx-auto shadow-lg shadow-[#5a20ff]/20">
        <svg className="w-8 h-8 text-white" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" d="M18 18.72a9.094 9.094 0 003.741-.479 3 3 0 00-4.682-2.72m.94 3.198l.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0112 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 016 18.719m12 0a5.971 5.971 0 00-.941-3.197m0 0A5.995 5.995 0 0012 12.75a5.995 5.995 0 00-5.058 2.772m0 0a3 3 0 00-4.681 2.72 8.986 8.986 0 003.74.477m.94-3.197a5.971 5.971 0 00-.94 3.197M15 6.75a3 3 0 11-6 0 3 3 0 016 0zm6 3a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0zm-13.5 0a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z" />
        </svg>
      </div>

      <h1 className="text-2xl font-bold text-slate-900 mt-5" style={{ fontFamily: "'Playfair Display', serif" }}>
        You're invited to join
      </h1>
      <p className="text-2xl font-bold text-[#5a20ff] mt-0.5" style={{ fontFamily: "'Playfair Display', serif" }}>
        {invite.orgName}
      </p>

      {invite.inviterName && (
        <p className="text-slate-500 mt-3 text-sm">
          <strong className="text-slate-700">{invite.inviterName}</strong> invited you
          as {invite.role === "admin" ? "an Admin" : "a Member"}
        </p>
      )}

      <p className="text-slate-400 text-xs mt-2">
        Invitation for <strong className="text-slate-500">{invite.email}</strong>
      </p>

      {/* Primary CTA */}
      <button
        onClick={handleJoin}
        className="mt-8 w-full max-w-xs mx-auto flex items-center justify-center gap-2 px-8 py-3.5 text-base font-semibold text-white rounded-xl transition-all duration-200 shadow-lg shadow-[#5a20ff]/25 hover:shadow-xl hover:scale-[1.02]"
        style={{ background: "linear-gradient(135deg, #5a20ff, #52dcb3)" }}
      >
        Join {invite.orgName}
      </button>

      {/* Secondary: already have an account */}
      <button
        onClick={handleLogin}
        className="mt-3 text-sm text-slate-500 hover:text-[#5a20ff] transition-colors"
      >
        Already have an account? <span className="font-medium underline underline-offset-2">Log in</span>
      </button>

      <p className="text-[11px] text-slate-300 mt-8">
        By joining, you agree to Scout's Terms of Service
      </p>
    </Shell>
  );
}

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex items-center justify-center p-6">
      <div className="w-full max-w-md text-center">
        {/* Logo */}
        <div className="mb-10">
          <span className="text-xl font-bold tracking-tight text-[#5a20ff]" style={{ fontFamily: "'Playfair Display', serif" }}>
            Scout
          </span>
          <span className="text-sm font-medium text-slate-400 ml-1">by EQ</span>
        </div>

        {/* Card */}
        <div className="bg-white rounded-2xl shadow-xl shadow-slate-200/50 border border-slate-100 px-8 py-10">
          {children}
        </div>
      </div>
    </div>
  );
}

function Spinner() {
  return (
    <div className="w-10 h-10 mx-auto">
      <svg className="animate-spin text-[#5a20ff]" viewBox="0 0 24 24" fill="none">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" />
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
      </svg>
    </div>
  );
}
